/**
 * ViewManager - Manages switching between Model and Panorama views
 */

export class ViewManager {
  constructor(modelView, panoramaView) {
    this.modelView = modelView;
    this.panoramaView = panoramaView;
    
    this.currentMode = 'model'; // 'model' or 'panorama'
    
    this.setupViewCallbacks();
  }

  /**
   * Initialize the application in model view
   */
  async initialize() {
    console.log('🎬 Initializing view manager...');
    
    // Start in model view
    this.modelView.enter();
    this.currentMode = 'model';
    
    console.log('✅ View manager initialized');
  }

  /**
   * Setup callbacks between views
   */
  setupViewCallbacks() {
    // When user clicks a hotspot in model view, enter that panorama
    this.modelView.onEnterPanorama(async (panoId, options) => {
      await this.enterPanorama(panoId, options);
    });
    
    // When user navigates in panorama view, switch to another panorama
    this.panoramaView.onNavigate(async (panoId) => {
      await this.panoramaView.navigateTo(panoId);
    });
  }

  /**
   * Enter panorama view
   * @param {string} panoId - The panorama ID to enter
   * @param {object} options - Optional settings to pass to panorama view
   */
  async enterPanorama(panoId, options = {}) {
    console.log(`🔄 ViewManager: Switching to panorama ${panoId}`);
    console.log(`   Options to pass:`, options);
    console.log(`   Current mode before: ${this.currentMode}`);
    
    // Exit model view
    console.log(`   Exiting model view...`);
    this.modelView.exit();
    
    // Enter panorama view
    console.log(`   Entering panorama view with options...`);
    await this.panoramaView.enter(panoId, options);
    
    this.currentMode = 'panorama';
    
    console.log(`✅ ViewManager: Now in panorama view: ${panoId}`);
  }

  /**
   * Return to model view
   */
  exitToModel() {
    console.log('🔄 ViewManager.exitToModel() called');
    console.log(`   Current mode: ${this.currentMode}`);
    
    if (this.currentMode === 'panorama') {
      console.log('   Exiting panorama view...');
      
      // Get current viewing direction from panorama view
      const viewingDirection = this.panoramaView.getCurrentViewingDirection();
      console.log(`   Got viewing direction from panorama: rotY=${viewingDirection.rotY.toFixed(1)}°`);
      
      // Exit panorama view
      this.panoramaView.exit();
      
      console.log('   Entering model view...');
      // Enter model view with viewing direction
      this.modelView.enter({ fromRotY: viewingDirection.rotY });
      
      this.currentMode = 'model';
      
      console.log('✅ ViewManager: Now in model view');
    } else {
      console.log('⚠️ ViewManager: Already in model view, nothing to do');
    }
  }

  /**
   * Toggle between views
   */
  toggle() {
    if (this.currentMode === 'model') {
      // Can't toggle to panorama without selecting one
      console.log('ℹ️ Click a marker to enter panorama view');
    } else {
      this.exitToModel();
    }
  }

  /**
   * Update current view
   */
  update() {
    if (this.currentMode === 'model') {
      this.modelView.update();
    } else if (this.currentMode === 'panorama') {
      this.panoramaView.update();
    }
  }

  /**
   * Get current mode
   */
  getCurrentMode() {
    return this.currentMode;
  }

  /**
   * Get current panorama ID (if in panorama mode)
   */
  getCurrentPanoramaId() {
    return this.currentMode === 'panorama' ? this.panoramaView.currentPanoId : null;
  }

  /**
   * Clean up
   */
  dispose() {
    this.modelView.dispose();
    this.panoramaView.dispose();
  }
}

